import { CompareCard } from '@folio/components/module-elements/CompareCard';
import { MAX_FLEET_SIZE } from '@folio/constants';
import { useAssertedParams } from '@folio/lib';
import { getLatestAsset } from '@folio/utils/useCurrentActiveAsset';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Asset,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  viewSpecificControllerListPolicyAssetsQueryOptions,
  type ListPolicyAssetsOutput,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';

type AssetCompareModuleProps = {
  /**
   * The branches to compare. If undefined, the base branch will be used.
   */
  branches: Array<string | undefined>;
};

export function AssetCompareModule({ branches }: AssetCompareModuleProps) {
  const { t } = useTranslation();
  const hasAssetInformations = useHasPolicyProductConfig('assetInformations');
  if (!hasAssetInformations) {
    return null;
  }

  const assetInformations = usePolicyProductConfig('assetInformations');

  const { policyId } = useAssertedParams();

  const results = useQueries({
    queries: branches.map(branchId => ({
      ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId, {
        branchId,
        count: MAX_FLEET_SIZE,
      }),
      select: (data: ListPolicyAssetsOutput): Asset | undefined =>
        getLatestAsset(
          data.data.map(asset => ({
            ...asset,
            customFields: keyValueFieldsToObject(asset.customFields ?? []),
          })),
        ),
    })),
  });

  const { data: productId } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId),
    select: data => data.productId,
  });

  if (!productId) {
    return null;
  }

  const assets = results
    .map(result => result.data)
    .filter((asset): asset is Asset => asset !== undefined);
  const isLoading = results.some(result => result.status === 'pending');

  return (
    <CompareCard
      title={t(`${productId}.assetCompare.title`)}
      productId={productId}
      fieldsConfig={assetInformations.fieldsConfig}
      entityType="asset"
      entities={assets}
      isLoading={isLoading}
    />
  );
}
